/**
 * Center Scope Tab - Members with explicit or org-wide scope access to this center
 */

import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { Globe, Building2 } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import apiClient from '../../services/api/apiClient';

interface CenterScopeTabProps {
  locationId: string;
}

interface ScopedMember {
  memberId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: { id: string; name: string }[];
  scopeType: 'org-wide' | 'explicit';
}

// ==================== Data fetching ====================

const getScopedMembers = async (locationId: string): Promise<ScopedMember[]> => {
  const response = await apiClient.get<{ success: boolean; data: any[] }>(
    `/api/v1/locations/${locationId}/scoped-members`,
  );
  return (response.data.data ?? []).map((m: any) => ({
    memberId: m.id,
    userId: m.user?.id ?? m.userId,
    firstName: m.user?.firstName ?? '',
    lastName: m.user?.lastName ?? '',
    email: m.user?.email ?? '',
    roles: (m.userRoles ?? []).map((ur: any) => ur.role),
    scopeType: m.isOrgWideScope ? 'org-wide' : 'explicit',
  }));
};

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const InfoBanner = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.info.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.info.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.info.text};

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 1px;
  }
`;

const TableWrapper = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;
`;

const Tr = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  vertical-align: middle;
`;

const SecondaryText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const RoleBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const ScopeIcon = styled.div<{ $isOrgWide: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme, $isOrgWide }) =>
    $isOrgWide ? theme.colors.status.info.text : theme.colors.text.secondary};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const SkeletonBar = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${({ $width }) => $width ?? '100%'};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

// ==================== Component ====================

export const CenterScopeTab = ({ locationId }: CenterScopeTabProps) => {
  const { data: members, isLoading } = useQuery({
    queryKey: ['location-scoped-members', locationId],
    queryFn: () => getScopedMembers(locationId),
  });

  const memberList = members ?? [];

  return (
    <Container>
      <SectionTitle>Center Scope</SectionTitle>

      <InfoBanner>
        <Globe />
        <span>
          This tab shows all members who have access to this center — either through an
          explicit center assignment or because they have org-wide scope.
        </span>
      </InfoBanner>

      <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>Member</Th>
              <Th>Email</Th>
              <Th>Roles</Th>
              <Th>Scope Type</Th>
            </tr>
          </Thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}>
                  <Td><SkeletonBar $width="140px" /></Td>
                  <Td><SkeletonBar $width="180px" /></Td>
                  <Td><SkeletonBar $width="90px" /></Td>
                  <Td><SkeletonBar $width="80px" /></Td>
                </tr>
              ))
            ) : memberList.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <EmptyState>No members have scope access to this center.</EmptyState>
                </td>
              </tr>
            ) : (
              memberList.map((member) => (
                <Tr key={member.memberId}>
                  <Td>
                    {member.firstName} {member.lastName}
                  </Td>
                  <Td>
                    <SecondaryText>{member.email}</SecondaryText>
                  </Td>
                  <Td>
                    <RoleBadges>
                      {(member.roles ?? []).map((r) => (
                        <Badge key={r.id} roleName={r.name}>
                          {r.name}
                        </Badge>
                      ))}
                    </RoleBadges>
                  </Td>
                  <Td>
                    <ScopeIcon $isOrgWide={member.scopeType === 'org-wide'}>
                      {member.scopeType === 'org-wide' ? (
                        <>
                          <Globe />
                          Org-wide
                        </>
                      ) : (
                        <>
                          <Building2 />
                          Explicit
                        </>
                      )}
                    </ScopeIcon>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrapper>
    </Container>
  );
};
